<template>
    <v-container fluid>
        <!-- Header -->
        <template-header :title="template ? template.name : 'Loading...'" :preview-active="previewMode"
            @back="$router.push('/evaluation/templates')" @add-section="openSectionDialog"
            @toggle-preview="togglePreviewMode" />

        <!-- Loading State -->
        <div v-if="loading" class="d-flex justify-center align-center py-5">
            <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
        </div>

        <!-- Error State -->
        <v-alert v-if="error" type="error" class="mb-4">
            {{ error }}
        </v-alert>

        <!-- Edit Mode Content -->
        <template v-if="!previewMode && template && !loading">
            <!-- Template Details -->
            <template-details :template="template" />

            <!-- Sections -->
            <section-list :sections="template.sections" @edit-section="editSection"
                @delete-section="confirmDeleteSection" @add-field="openFieldDialog" @edit-field="editField"
                @delete-field="confirmDeleteField" />
        </template>

        <!-- Preview Mode Content -->
        <template v-if="previewMode && template && !loading">
            <template-preview :template="template" />
        </template>

        <!-- Section Dialog -->
        <section-dialog v-model="sectionDialog" :editing-section="editingSection" :loading="saving"
            @save="saveSection" />

        <!-- Field Dialog -->
        <field-dialog v-model="fieldDialog" :editing-field="editingField" :loading="saving"
            :sections="template.sections" @save="saveField" />

        <!-- Delete Section Dialog -->
        <delete-confirmation v-model="deleteSectionDialog" title="Delete Section"
            message="Are you sure you want to delete this section? This action cannot be undone." :loading="deleting"
            @confirm="deleteSection" />

        <!-- Delete Field Dialog -->
        <delete-confirmation v-model="deleteFieldDialog" title="Delete Field"
            message="Are you sure you want to delete this field? This action cannot be undone." :loading="deleting"
            @confirm="deleteFieldConfirmed" />
    </v-container>
</template>

<script>
import { Component, Vue } from 'vue-property-decorator';
import { getTemplateByIdApi } from '@/shared/apis/aper/aperTemplate';
import { createSectionApi, getSectionsApi, updateSectionApi, deleteSectionApi } from '@/shared/apis/aper/aperSections';
import { getFieldsBySection, createField, updateField, deleteField } from '@/shared/apis/aper/aperFields';
import { Template, Section, Field, FieldType } from '@/shared/types/aperTemplate';
import { Route } from 'vue-router';
import AppView from "@/layout/AppView.vue";

// Components
import TemplateHeader from './components/TemplateHeader.vue';
import TemplateDetails from './components/TemplateDetails.vue';
import TemplatePreview from './components/TemplatePreview.vue';
import SectionList from './components/sections/SectionList.vue';
import SectionDialog from './components/sections/SectionDialog.vue';
import FieldDialog from './components/fields/FieldDialog.vue';
import DeleteConfirmation from './components/dialogs/DeleteConfirmation.vue';

@Component({
    components: {
        AppView,
        TemplateHeader,
        TemplateDetails,
        TemplatePreview,
        SectionList,
        SectionDialog,
        FieldDialog,
        DeleteConfirmation
    }
})
export default class TemplateView extends Vue {
    // Data properties
    template = null;
    loading = false;
    error = null;
    previewMode = false;
    sectionDialog = false;
    fieldDialog = false;
    deleteSectionDialog = false;
    deleteFieldDialog = false;
    saving = false;
    deleting = false;
    editingSection = null;
    editingField = null;
    selectedSection = null;
    // Lifecycle hooks
    async created() {
        const templateId = this.$route.params.id;
        if (templateId) {
            await this.fetchTemplate(templateId);
            await this.loadSections(templateId);
        }
    }

    // Methods
    async fetchTemplate(id) {
        this.loading = true;
        this.error = null;
        try {
            const response = await getTemplateByIdApi(id);
            this.template = response.data;
        } catch (error) {
            this.error = 'Failed to load template';
            console.error('Error fetching template:', error);
        } finally {
            this.loading = false;
        }
    }

    openSectionDialog() {
        this.editingSection = null;
        this.sectionDialog = true;
    }

    editSection(section) {
        this.editingSection = section;
        this.sectionDialog = true;
    }

    async saveSection(sectionData) {
        this.saving = true;
        try {
            const templateId = this.$route.params.id;
            const data = {
                name: sectionData.name,
                description: sectionData.description,
                displayOrder: Number(sectionData.displayOrder),
                role: sectionData.role
            };

            if (this.editingSection) {
                // Update existing section
                await updateSectionApi(templateId, this.editingSection.id, data);
                const index = this.template.sections.findIndex(s => s.id === this.editingSection.id);
                if (index !== -1) {
                    this.template.sections[index] = { ...this.template.sections[index], ...data };
                }
            } else {
                // Add new section
                const response = await createSectionApi(templateId, data);
                this.template.sections.push(response.data);
            }
            this.sectionDialog = false;
            this.$toast.success(`Section ${this.editingSection ? 'updated' : 'created'} successfully`);
        } catch (error) {
            console.error('Error saving section:', error);
            this.$toast.error(`Failed to ${this.editingSection ? 'update' : 'create'} section`);
        } finally {
            this.saving = false;
        }
    }

    confirmDeleteSection(section) {
        this.selectedSection = section;
        this.deleteSectionDialog = true;
    }

    async deleteSection() {
        if (!this.selectedSection) return;

        this.deleting = true;
        try {
            const templateId = this.$route.params.id;
            await deleteSectionApi(templateId, this.selectedSection.id);

            // Update local state
            const index = this.template.sections.findIndex(s => s.id === this.selectedSection.id);
            if (index !== -1) {
                this.template.sections.splice(index, 1);
            }
            this.$toast.success('Section deleted successfully');
            this.deleteSectionDialog = false;
        } catch (error) {
            console.error('Error deleting section:', error);
            this.$toast.error('Failed to delete section');
        } finally {
            this.deleting = false;
        }
    }

    openFieldDialog(section) {
        this.selectedSection = section;
        this.editingField = null;
        this.fieldDialog = true;
    }

    editField(section, field) {
        this.selectedSection = section;
        this.editingField = field;
        this.fieldDialog = true;
    }

    async saveField(fieldData) {
        if (!this.selectedSection) return;

        this.saving = true;
        try {
            const sectionId = this.selectedSection && this.selectedSection.id ? this.selectedSection.id.toString() : undefined;
            const data = {
                name: fieldData.name,
                label: fieldData.label,
                fieldCode: fieldData.fieldCode,
                description: fieldData.description,
                hint: fieldData.hint,
                displayOrder: fieldData.displayOrder,
                fieldType: fieldData.fieldType,
                required: fieldData.required,
                minLength: fieldData.minLength,
                maxLength: fieldData.maxLength,
                minEntries: fieldData.minEntries,
                maxEntries: fieldData.maxEntries,
                requiresLabels: fieldData.requiresLabels,
                textEntriesPlaceholder: fieldData.textEntriesPlaceholder,
                minValue: fieldData.minValue,
                maxValue: fieldData.maxValue,
                stepValue: fieldData.stepValue,
                minRating: fieldData.minRating,
                maxRating: fieldData.maxRating,
                minDate: fieldData.minDate,
                maxDate: fieldData.maxDate,
                options: fieldData.options,
                textTemplates: fieldData.textTemplates,
                subFields: fieldData.subFields,
                showInReport: fieldData.showInReport,
                reportOrder: fieldData.reportOrder,
                includeInScoring: fieldData.includeInScoring,
                maximumScore: fieldData.maximumScore,
                useAggregate: fieldData.useAggregate,
                predefinedFieldType: fieldData.predefinedFieldType,
                scoreSectionId: fieldData.scoreSectionId,
                role: fieldData.role
            };

            if (this.editingField) {
                // Update existing field
                const fieldId = this.editingField && this.editingField.id ? this.editingField.id.toString() : undefined;
                await updateField(sectionId, fieldId, data);

                // Update local state
                const index = this.selectedSection.fields.findIndex(f => f.id === this.editingField.id);
                if (index !== -1) {
                    this.selectedSection.fields[index] = { ...this.selectedSection.fields[index], ...data };
                }
                this.$toast.success('Field updated successfully');
            } else {
                // Add new field
                const response = await createField(sectionId, data);
                this.selectedSection.fields.push(response.data);
                this.$toast.success('Field created successfully');
            }
            this.fieldDialog = false;
        } catch (error) {
            console.error('Error saving field:', error);
            this.$toast.error(`Failed to ${this.editingField ? 'update' : 'create'} field`);
        } finally {
            this.saving = false;
        }
    }

    async loadSections(templateId) {
        try {
            const response = await getSectionsApi(templateId);
            this.template.sections = response.data;

            // Load fields for each section
            for (const section of this.template.sections) {
                await this.loadFields(section);
            }
        } catch (error) {
            console.error('Error loading sections:', error);
            this.$toast.error('Failed to load sections');
        }
    }

    async loadFields(section) {
        try {
            const response = await getFieldsBySection(section.id.toString());
            section.fields = response.data;
        } catch (error) {
            console.error(`Error loading fields for section ${section.id}:`, error);
            // Initialize with empty fields array if fails
            section.fields = section.fields || [];
        }
    }

    confirmDeleteField(section, field) {
        this.selectedSection = section;
        this.editingField = field;
        this.deleteFieldDialog = true;
    }

    async deleteFieldConfirmed() {
        if (!this.selectedSection || !this.editingField) return;

        this.deleting = true;
        try {
            const sectionId = this.selectedSection.id?.toString();
            const fieldId = this.editingField.id?.toString();

            await deleteField(sectionId, fieldId);

            // Update local state
            const index = this.selectedSection.fields.findIndex(f => f.id === this.editingField.id);
            if (index !== -1) {
                this.selectedSection.fields.splice(index, 1);
            }

            this.$toast.success('Field deleted successfully');
            this.deleteFieldDialog = false;
        } catch (error) {
            console.error('Error deleting field:', error);
            this.$toast.error('Failed to delete field');
        } finally {
            this.deleting = false;
        }
    }

    togglePreviewMode() {
        this.previewMode = !this.previewMode;

        // If entering preview mode, close any open dialogs
        if (this.previewMode) {
            this.sectionDialog = false;
            this.fieldDialog = false;
            this.deleteSectionDialog = false;
            this.deleteFieldDialog = false;
        }
    }
}
</script>
